import {
  MATCH,
  TACTICS,
  fieldPlayersPerTeam,
  type MatchState,
  type TeamInstructions,
  type TeamSide,
} from "@golazo/shared";
import { clamp } from "../core/math.js";

/** Lo que la IA mira del partido: el marcador y el reloj, nada mas. */
export type MatchClock = Pick<MatchState, "score" | "timeRemaining" | "period" | "periods" | "goldenGoal">;

/**
 * Fraccion del partido jugada, de 0 al saque inicial a 1 al final del ultimo
 * tiempo (y en el gol de oro). Cuenta los tiempos que quedan por jugar: en un
 * partido a dos tiempos, el final del primero es la mitad.
 */
export function matchElapsed(clock: MatchClock): number {
  if (clock.goldenGoal) return 1;
  const periods = Math.max(1, clock.periods);
  const total = periods * MATCH.DURATION;
  if (total <= 0) return 1;
  const left = clock.timeRemaining + Math.max(0, periods - clock.period) * MATCH.DURATION;
  return clamp(1 - left / total, 0, 1);
}

/**
 * Lo que el marcador y el reloj le piden a `side`: +1 ir a por el gol, -1
 * proteger lo que tiene, 0 jugar su plan.
 *
 * Empatados es SIEMPRE 0, tambien en el gol de oro: no hay nada que remontar
 * ni que proteger, y el que marca gana — cada uno juega lo suyo. Y como hasta
 * el primer gol no hay diferencia, hasta entonces la IA es exactamente la de
 * antes de leer el marcador.
 *
 * Solo hace aritmetica (sin `Math.pow` ni funciones que cambien entre
 * motores): entra en la simulacion y el arbitro del servidor la rehace.
 */
export function matchUrgency(side: TeamSide, clock: MatchClock): number {
  const other: TeamSide = side === "home" ? "away" : "home";
  const diff = clock.score[side] - clock.score[other];
  if (diff === 0) return 0;
  const gap = Math.min(3, Math.abs(diff)) - 1;
  const chasing = diff < 0;
  const start = chasing && gap >= 1 ? TACTICS.SITUATION_START_BIG_DEFICIT : TACTICS.SITUATION_START;
  const ramp = clamp(
    (matchElapsed(clock) - start) / (TACTICS.SITUATION_FULL - start),
    0,
    1,
  );
  if (ramp <= 0) return 0;
  return chasing
    ? ramp * TACTICS.SITUATION_CHASE_BY_DEFICIT[gap]!
    : -ramp * TACTICS.SITUATION_PROTECT_BY_LEAD[gap]!;
}

/**
 * El plan del equipo ajustado a la urgencia, escrito en `out` (se reutiliza
 * tick a tick para no crear basura). Con urgencia 0 devuelve `base` TAL CUAL,
 * el mismo objeto: el plan que el jugador toca en caliente sigue siendo el que
 * se lee.
 *
 * Suma o resta sobre el plan en vez de sustituirlo, que es lo que haria un
 * entrenador de Football Manager: "mas arriba" o "a cerrar" parten de como
 * juega ese equipo, no de una tactica comun para todos.
 */
export function situationalInstructions(
  base: TeamInstructions,
  urgency: number,
  out: TeamInstructions,
): TeamInstructions {
  if (urgency === 0) return base;
  Object.assign(out, base);
  if (urgency > 0) {
    out.pressing = clamp(base.pressing + urgency * TACTICS.CHASE_PRESSING, 0, 1);
    // Adelantar el bloque solo donde hay gente para cubrir (ver
    // `TACTICS.CHASE_SHAPE_SMALL`).
    const c =
      fieldPlayersPerTeam() >= TACTICS.SITUATION_PUSH_MIN_OUTFIELD
        ? urgency
        : urgency * TACTICS.CHASE_SHAPE_SMALL;
    if (c === 0) return out;
    out.mentality = clamp(base.mentality + c * TACTICS.CHASE_MENTALITY, -1, 1);
    out.lineHeight = clamp(base.lineHeight + c * TACTICS.CHASE_LINE_HEIGHT, 0, 1);
    out.lateRuns = clamp(base.lateRuns + c * TACTICS.CHASE_LATE_RUNS, 0, 1);
    out.directness = clamp(base.directness + c * TACTICS.CHASE_DIRECTNESS, 0, 1);
    out.tempo = clamp(base.tempo + c * TACTICS.CHASE_TEMPO, 0, 1);
    return out;
  }
  const p = -urgency;
  out.mentality = clamp(base.mentality - p * TACTICS.PROTECT_MENTALITY, -1, 1);
  out.pressing = clamp(base.pressing - p * TACTICS.PROTECT_PRESSING, 0, 1);
  out.lineHeight = clamp(base.lineHeight - p * TACTICS.PROTECT_LINE_HEIGHT, 0, 1);
  out.lateRuns = clamp(base.lateRuns - p * TACTICS.PROTECT_LATE_RUNS, 0, 1);
  out.tempo = clamp(base.tempo - p * TACTICS.PROTECT_TEMPO, 0, 1);
  out.compactness = clamp(base.compactness + p * TACTICS.PROTECT_COMPACTNESS, 0, 1);
  return out;
}
